import React from 'react';
import {
  Typography, Button, Box, Grid, Dialog, DialogTitle, DialogContent, DialogActions,
  Card, CardMedia, CardContent, Alert, Chip
} from '@mui/material';
import ThermostatIcon from '@mui/icons-material/Thermostat';
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import { 
  validateBuildSpace,
  PA12_BUILD_SPACE,
  SILICONE_BUILD_SPACE
} from '../Utils/PriceCalculator';

interface MaterialSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onSelect: (materialId: number) => void;
  currentModelId: number | null;
  models: any[];
  formatPrice: (price: number) => string;
  materials: any[];
  calculateItemPrice: (model: any, material: any) => number;
}

const MaterialSelectionModal: React.FC<MaterialSelectionModalProps> = ({
  open,
  onClose,
  onSelect,
  currentModelId,
  models,
  formatPrice,
  materials,
  calculateItemPrice
}) => {
  const currentModel = models.find(m => m.id === currentModelId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Material auswählen</DialogTitle>
      <DialogContent>
        {/* Show error if model is too large for all materials */}
        {currentModel?.dimensions && (() => {
          const pa12Validation = validateBuildSpace(currentModel.dimensions, false);
          const siliconValidation = validateBuildSpace(currentModel.dimensions, true);
          
          if (!pa12Validation.valid && !siliconValidation.valid) {
            return (
              <Alert severity="error" sx={{ mb: 2 }}>
                Modell überschreitet den maximalen Bauraum aller verfügbaren Materialien
              </Alert>
            );
          }
          return null;
        })()}

        <Grid container spacing={2}>
          {materials.map((material) => {
            const itemPrice = currentModel ? calculateItemPrice(currentModel, material) : 0;

            // Check if this material is valid for the current model
            const validation = currentModel?.dimensions 
              ? validateBuildSpace(currentModel.dimensions, !material.isPlastic)
              : { valid: true };

            const buildSpace = material.isPlastic ? PA12_BUILD_SPACE : SILICONE_BUILD_SPACE;

            return (
              <Grid item xs={12} key={material.id}>
                <Card 
                  sx={{ 
                    cursor: validation.valid ? 'pointer' : 'not-allowed',
                    opacity: validation.valid ? 1 : 0.7,
                    '&:hover': validation.valid ? { boxShadow: 6 } : {},
                    border: currentModel?.material === material.id ? `2px solid ${material.color}` : 'none',
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    height: { md: '220px' }
                  }}
                  onClick={() => validation.valid && onSelect(material.id)}
                >
                  <CardMedia
                    component="img"
                    sx={{
                      width: { xs: '100%', md: '200px' },
                      height: { xs: '160px', md: '220px' },
                      objectFit: 'cover',
                      flexShrink: 0
                    }}
                    image={material.image}
                    alt={material.name}
                  />

                  <CardContent sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    py: 2,
                    height: '100%',
                    overflow: 'visible'
                  }}>
                    <Box>
                      <Typography variant="h6" component="div" gutterBottom>
                        {material.name}
                      </Typography>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                        {material.temperature && (
                          <Chip
                            icon={<ThermostatIcon />}
                            label={`Hält bis zu ${material.temperature}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        )}

                        {material.isPlastic && (
                          <Chip
                            icon={<FormatPaintIcon />}
                            label="Lackierbar"
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {material.additionalInfo}
                      </Typography>

                      {/* Show build space info and warning if applicable */}
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Max. Bauraum: {buildSpace.width}x{buildSpace.height}x{buildSpace.depth}mm
                      </Typography>

                      {!validation.valid && (
                        <Alert severity="warning">
                          {validation.message}
                        </Alert>
                      )}
                    </Box>

                    <Box sx={{ mt: 'auto', pt: 1, pb: 2, marginBottom: 20 }}>
                      {currentModel && (
                        <>
                          <Typography variant="h6" color="primary">
                            {formatPrice(itemPrice)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Preis pro Stück: {formatPrice(currentModel.quantity && currentModel.quantity > 1 ? itemPrice / currentModel.quantity : itemPrice)}
                          </Typography>
                        </>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Abbrechen</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaterialSelectionModal;